import { useMemo } from 'react';
import { useOtherSettings } from '@namespace/cms';
import {
  CASINO_TYPE,
  FAVORITE_CATEGORY,
  PLEX_CUSTOM_CATEGORY_MAX_GAMES_PAGES_LIMIT,
  PLEX_LIMIT,
  RECENTLY_PLAYED_CATEGORY,
  RECOMMENDED_CATEGORY
} from '../../../constants';
import { useGamesStore, useGetLiveRtpGamesSectionsIds } from '../../../store';
import { useCasinoSettings } from '../../useCasinoSettings';
import { useGetFeedName } from '../../useGetFeedName';
import { useFavoritesCustomCategories } from './useFavoritesCustomCategories';
import { useGetPlexPaginationCommonData } from './useGetPlexPaginationCommonData';

export const useCustomCategoriesData = ({
  category,
  casinoType,
  pageSize,
  isInitialDataLoaded
}) => {
  const { recentlyPlayed, recommendedGames, dynamicCategories } = useGamesStore(
    ['recentlyPlayed', 'dynamicCategories', 'recommendedGames']
  );
  const { plexPagination, formattedPage } = useGetPlexPaginationCommonData();

  const { suggesterServiceEntityName } = useCasinoSettings(casinoType);
  const { casinoDynamicCategoriesIds = {} } = useOtherSettings();
  const feedName = useGetFeedName(casinoType);

  const liveRtpGamesSectionsIds = useGetLiveRtpGamesSectionsIds({
    feedName
  });

  // handle removing favourite game for favourite category
  // getting data about favorites from service and define if category is favorite
  const {
    isFavoriteCategory,
    favoriteCategoryGamesIds,
    isFavoritesLoaded,
    instantFavoritesPlexRequestPageSize
  } = useFavoritesCustomCategories({ casinoType, category });

  const categoryGames = useMemo(
    () =>
      plexPagination.category?.id === category
        ? plexPagination.category?.gamesIds?.slice(
            0,
            pageSize * formattedPage
          ) || []
        : undefined,
    [plexPagination, pageSize, category, formattedPage]
  );

  const plexRequestPageSize =
    isFavoriteCategory && casinoType === CASINO_TYPE.INSTANT_GAMES
      ? instantFavoritesPlexRequestPageSize
      : PLEX_LIMIT / PLEX_CUSTOM_CATEGORY_MAX_GAMES_PAGES_LIMIT;

  const { wagerSlots: wagerSlotsCategoryId, liveRtp: liveRtpCategoryId } =
    casinoDynamicCategoriesIds;

  // recommended / recently / wager / liveRtp(cold-hots) - taken from suggester service which sends games launch ids
  const customCategoriesLaunchIds = useMemo(
    () => ({
      recommended: recommendedGames?.[suggesterServiceEntityName]?.ids,
      recentlyPlayed: recentlyPlayed?.[suggesterServiceEntityName]?.ids,
      ...(wagerSlotsCategoryId
        ? {
            [wagerSlotsCategoryId]: dynamicCategories?.wagerSlotGames?.ids
          }
        : {}),
      ...(liveRtpCategoryId
        ? {
            // livertp hot+cold = 28, request plexRequestPageSize(50) games in total
            [liveRtpCategoryId]: [
              ...(dynamicCategories?.liveRtpGames?.hot?.slice(
                0,
                plexRequestPageSize / 2
              ) ?? []),
              ...(dynamicCategories?.liveRtpGames?.cold?.slice(
                0,
                plexRequestPageSize / 2
              ) ?? [])
            ]
          }
        : {})
    }),
    [
      recommendedGames,
      recentlyPlayed,
      suggesterServiceEntityName,
      wagerSlotsCategoryId,
      dynamicCategories,
      liveRtpCategoryId,
      plexRequestPageSize
    ]
  );

  const customCategoriesIsLoaded = useMemo(
    () => ({
      [wagerSlotsCategoryId]: dynamicCategories?.wagerSlotGames?.isLoaded,
      [liveRtpCategoryId]: dynamicCategories?.liveRtpGames?.isLoaded,
      [FAVORITE_CATEGORY.id]: isFavoritesLoaded,
      [RECOMMENDED_CATEGORY.id]:
        recommendedGames?.[suggesterServiceEntityName]?.isLoaded,
      [RECENTLY_PLAYED_CATEGORY.id]:
        recentlyPlayed?.[suggesterServiceEntityName]?.isLoaded
    }),
    [
      wagerSlotsCategoryId,
      dynamicCategories,
      liveRtpCategoryId,
      isFavoritesLoaded,
      suggesterServiceEntityName,
      recentlyPlayed,
      recommendedGames
    ]
  );

  const multiSectionCategories = {
    [liveRtpCategoryId]: liveRtpGamesSectionsIds
  };

  const isMultiSectionCategory = category in multiSectionCategories;

  const categoryGameIds = useMemo(
    () =>
      isFavoriteCategory
        ? favoriteCategoryGamesIds || []
        : customCategoriesLaunchIds[category] || [],
    [
      customCategoriesLaunchIds,
      favoriteCategoryGamesIds,
      category,
      isFavoriteCategory
    ]
  );

  const categoriesAllGamesLoaded = useMemo(() => {
    const arr = [
      RECENTLY_PLAYED_CATEGORY.id,
      RECOMMENDED_CATEGORY.id,
      liveRtpCategoryId
    ];
    if (casinoType === CASINO_TYPE.INSTANT_GAMES) {
      arr.push(FAVORITE_CATEGORY.id);
    }
    return arr.filter(
      (categoryName) => customCategoriesIsLoaded[categoryName] && categoryGames
    );
  }, [liveRtpCategoryId, customCategoriesIsLoaded, categoryGames, casinoType]);

  const customCategoryRequestedIds = categoryGameIds.slice(
    0,
    plexRequestPageSize * formattedPage
  );
  const actualIsAllGamesLoaded =
    (customCategoriesIsLoaded[category] &&
      isInitialDataLoaded &&
      customCategoryRequestedIds.length === categoryGameIds.length &&
      categoryGames?.length === plexPagination.category?.gamesIds?.length) ||
    categoriesAllGamesLoaded.includes(category);

  return {
    multiSectionCategories,
    actualIsAllGamesLoaded,
    isMultiSectionCategory,
    isFavoriteCategory,
    categoryGameIds,
    plexRequestPageSize,
    categoryGames
  };
};
